import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ExternalTestHealth, ExternalTestProvider, ExternalTestVerdict } from '@chatsdet/types';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';

export type ExternalTestCatalogSortBy = 'displayName' | 'lastSeenAt' | 'reliability' | 'totalRuns';

export type ExternalTestCatalogQuery = {
  page: number;
  pageSize: number;
  search?: string;
  suiteIds?: string[];
  sourceKeys?: string[];
  providers?: ExternalTestProvider[];
  health?: ExternalTestHealth[];
  sortBy?: ExternalTestCatalogSortBy;
  sortOrder?: 'asc' | 'desc';
};

export type ExternalTestCatalogRow = {
  id: string;
  sourceKind: 'github' | 'upload' | 'image';
  sourceKey: string;
  provider: ExternalTestProvider;
  testKey: string;
  displayName: string;
  suitePath: string | null;
  granularity: string;
  lastStatus: 'passed' | 'flaky' | 'failed' | 'skipped' | null;
  health: ExternalTestHealth;
  totalRuns: number;
  passCount: number;
  failCount: number;
  flakyCount: number;
  reliability: number | null;
  firstSeenAt: string;
  lastSeenAt: string;
  latestRunId: string | null;
  latestItemId: string | null;
  suiteCount: number;
  suites: Array<{ id: string; name: string; archived: boolean }>;
};

export type ExternalTestCatalogSource = {
  sourceKind: 'github' | 'upload' | 'image';
  sourceKey: string;
  name: string;
};

export type ExternalTestCatalogResponse = {
  ok: true;
  data: {
    tests: ExternalTestCatalogRow[];
    sources: ExternalTestCatalogSource[];
    suites: Array<{ id: string; name: string; archived: boolean }>;
  };
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
};

const catalogKeys = {
  all: ['external-test-catalog'] as const,
  list: (query: ExternalTestCatalogQuery) =>
    [
      ...catalogKeys.all,
      query.page,
      query.pageSize,
      query.search ?? '',
      (query.suiteIds ?? []).join(','),
      (query.sourceKeys ?? []).join(','),
      (query.providers ?? []).join(','),
      (query.health ?? []).join(','),
      query.sortBy ?? 'lastSeenAt',
      query.sortOrder ?? 'desc',
    ] as const,
};

export function buildExternalTestCatalogQuery(query: ExternalTestCatalogQuery): string {
  const params = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
  });
  if (query.search) params.set('q', query.search);
  if (query.suiteIds?.length) params.set('suiteIds', query.suiteIds.join(','));
  if (query.sourceKeys?.length) params.set('sourceKeys', query.sourceKeys.join(','));
  if (query.providers?.length) params.set('providers', query.providers.join(','));
  if (query.health?.length) params.set('health', query.health.join(','));
  if (query.sortBy) params.set('sortBy', query.sortBy);
  if (query.sortOrder) params.set('sortOrder', query.sortOrder);
  return params.toString();
}

async function fetchExternalTestCatalog(
  query: ExternalTestCatalogQuery
): Promise<ExternalTestCatalogResponse> {
  const response = await fetch(
    `${API_URL}/external-tests/catalog?${buildExternalTestCatalogQuery(query)}`,
    { credentials: 'include' }
  );
  if (!response.ok) throw await toApiError(response, 'Failed to load external tests');
  const body = (await response.json()) as ExternalTestCatalogResponse;
  if (!body?.ok) throw new Error('Failed to load external tests');
  return body;
}

export function useExternalTestCatalog(
  query: ExternalTestCatalogQuery
): UseQueryResult<ExternalTestCatalogResponse> {
  return useQuery({
    queryKey: catalogKeys.list(query),
    queryFn: () => fetchExternalTestCatalog(query),
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });
}

export type ExternalTestCatalogRecentRun = {
  runId: string;
  runStatus: 'open' | 'collecting' | 'sealed' | 'canceled';
  verdict: ExternalTestVerdict;
  /** When the run finished, or started if still open. */
  at: string;
};

export type ExternalTestCatalogRecentRunsResponse = {
  ok: true;
  data: { runsByTest: Record<string, ExternalTestCatalogRecentRun[]> };
};

async function fetchExternalTestCatalogRecentRuns(
  testIds: string[]
): Promise<Record<string, ExternalTestCatalogRecentRun[]>> {
  const params = new URLSearchParams({ testIds: testIds.join(',') });
  const response = await fetch(`${API_URL}/external-tests/catalog/recent-runs?${params}`, {
    credentials: 'include',
  });
  if (!response.ok) throw await toApiError(response, 'Failed to load recent runs');
  const body = (await response.json()) as ExternalTestCatalogRecentRunsResponse;
  if (!body?.ok) throw new Error('Failed to load recent runs');
  return body.data.runsByTest;
}

/**
 * Each visible test's last 10 runs, fetched separately from the catalog page
 * so the paginated list payload stays skinny. Keyed on the current page's
 * test ids; `placeholderData` keeps the previous page's bars visible while
 * the next page's runs load, avoiding a flash to empty slots.
 */
export function useExternalTestCatalogRecentRuns(
  testIds: string[]
): UseQueryResult<Record<string, ExternalTestCatalogRecentRun[]>> {
  return useQuery({
    queryKey: ['external-test-catalog-recent-runs', ...testIds],
    queryFn: () => fetchExternalTestCatalogRecentRuns(testIds),
    enabled: testIds.length > 0,
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });
}
